<template>
  <v-dialog :value="value" max-width="640" persistent @input="$emit('input', $event)">
    <v-card v-if="dialog && form">
      <v-card-title class="primary">
        <span class="white--text">{{ dialog.title }}</span>
      </v-card-title>
      <v-card-text class="pt-4 black--text">
        <p v-for="(line, i) in dialog.body" :key="`body-${i}`" class="mb-2">{{ line }}</p>
        <v-alert v-for="(a, i) in dialog.alerts" :key="`alert-${i}`" :type="a.type" dense outlined class="mb-3">{{ a.text }}</v-alert>
        <v-text-field
          v-for="f in dialog.fields"
          :key="`field-${f.key}`"
          v-model="form.values[f.key]"
          :label="f.label"
          :hint="f.hint"
          :counter="f.maxlength"
          :maxlength="f.maxlength"
          persistent-hint
          outlined
          dense
          class="mt-2"
          autocomplete="off"
        />
        <div v-if="dialog.window" class="mt-2">
          <div class="subtitle-2 mb-2">{{ dialog.window.label }} (Sydney time)</div>
          <v-row dense align="start">
            <v-col cols="12" md="4">
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.window.date"
                    label="Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    :disabled="form.notBooked"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.window.date" :min="today" @input="dateMenu = false" />
              </v-menu>
            </v-col>
            <v-col cols="6" md="4">
              <v-select v-model="form.window.start" :items="starts" label="Start (Sydney time)" dense outlined hide-details :disabled="form.notBooked" />
            </v-col>
            <v-col cols="6" md="4">
              <v-select v-model="form.window.duration" :items="durations" label="Duration (min)" dense outlined hide-details :disabled="form.notBooked" />
            </v-col>
          </v-row>
          <div v-if="summary" class="mt-1 grey--text text--darken-2">{{ summary }}</div>
          <v-checkbox v-model="form.notBooked" :label="dialog.window.notBookedLabel" dense hide-details class="mt-1" />
        </div>
        <v-checkbox
          v-for="tick in dialog.ticks"
          :key="`tick-${tick.key}`"
          v-model="form.ticks[tick.key]"
          :label="tick.label"
          dense
          hide-details
          class="mt-0 mb-2"
        />
        <template v-if="dialog.requireTyping">
          <p class="mt-3 mb-1">Type the CVC name to continue.</p>
          <v-text-field v-model="form.typed" :placeholder="cvcName" autocomplete="off" outlined dense hide-details />
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn color="error" :disabled="!ready" @click="proceed">{{ dialog.proceedLabel }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  WINDOW_DURATIONS,
  WINDOW_STARTS,
  sydneyDate,
  windowFromForm,
  windowSummary,
} from "@/utils/cvcMigrationPage";
import { emptyForm, formReady } from "@/utils/cvcMigrationRollbackAfter";

// A step dialog with several inputs (start rollback, the Optus window, confirm the
// re-point back): text fields, a Sydney-time window with a "not booked yet" tick, ticks
// and the typed CVC name. The rules are in cvcMigrationRollbackAfter.js: this renders the
// dialog it is given and hands the filled form back.
export default {
  name: "RollbackFormDialog",
  props: {
    value: { type: Boolean, default: false },
    dialog: { type: Object, default: null },
    ctx: { type: Object, default: null },
    cvcName: { type: String, default: "" },
    now: { type: Date, default: () => new Date() },
  },
  data () {
    return {
      form: this.dialog && this.ctx ? emptyForm(this.dialog, this.ctx) : null,
      dateMenu: false,
      starts: WINDOW_STARTS,
      durations: WINDOW_DURATIONS,
    };
  },
  computed: {
    ready () {
      return !!this.form && formReady(this.dialog, this.form, this.cvcName);
    },
    today () {
      return sydneyDate(this.now);
    },
    summary () {
      const w = this.form && !this.form.notBooked ? windowFromForm(this.form.window) : null;
      return w ? windowSummary(w.startUtc, w.endUtc, this.ctx.poiState) : "";
    },
  },
  watch: {
    // Every opening starts from the dialog's own start.
    value (open) {
      if (open && this.dialog && this.ctx) this.form = emptyForm(this.dialog, this.ctx);
    },
  },
  methods: {
    cancel () {
      this.$emit("input", false);
      this.$emit("cancel");
    },
    proceed () {
      if (!this.ready) return;
      this.$emit("proceed", this.form);
      this.$emit("input", false);
    },
  },
};
</script>
